import { useState, useCallback } from "react";
import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
	ArrowLeft,
	RefreshCw,
	AlertCircle,
	CheckCircle2,
	Clock,
	XCircle,
	Play,
	Pause,
} from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { BaseApiClient } from "@/lib/api/baseApiClient.service";

interface PipelineChapter {
	id: string;
	index: number;
	title: string;
	scriptStatus: string;
	audioStatus: string;
	overallStatus: string;
	hasScript: boolean;
	hasAudio: boolean;
	retryCount: number;
	lastError: string | null;
	timings: {
		scriptStarted: string | null;
		scriptCompleted: string | null;
		audioStarted: string | null;
		audioCompleted: string | null;
		scriptDuration: number | null;
		audioDuration: number | null;
	};
}

interface PipelinePodcast {
	id: string;
	title: string;
	status: string;
	totalChapters: number;
	chaptersCompleted: number;
	createdAt: string;
	user: {
		email: string;
		name: string | null;
	};
	pipeline: {
		scriptGeneration: {
			pending: number;
			processing: number;
			success: number;
			failed: number;
			skipped: number;
		};
		audioGeneration: {
			pending: number;
			processing: number;
			success: number;
			failed: number;
			skipped: number;
		};
	};
	health: {
		bottlenecks: string[];
		warnings: string[];
		recommendations: string[];
	};
}

const StatusBadge = ({ status }: { status: string }) => {
	const statusConfig: Record<
		string,
		{
			label: string;
			variant: "default" | "secondary" | "destructive" | "outline";
		}
	> = {
		PENDING: { label: "Pending", variant: "secondary" },
		PROCESSING: { label: "Processing", variant: "default" },
		SUCCESS: { label: "Success", variant: "outline" },
		FAILED: { label: "Failed", variant: "destructive" },
		SKIPPED: { label: "Skipped", variant: "secondary" },
		QUEUED: { label: "Queued", variant: "secondary" },
		SUBMITTED: { label: "Submitted", variant: "default" },
		COMPLETED: { label: "Completed", variant: "outline" },
	};

	const config = statusConfig[status] || {
		label: status,
		variant: "secondary" as const,
	};

	return <Badge variant={config.variant}>{config.label}</Badge>;
};

const StatusIcon = ({ status }: { status: string }) => {
	const icons: Record<string, ReactElement> = {
		PENDING: <Clock className="h-4 w-4 text-gray-400" />,
		PROCESSING: <Play className="h-4 w-4 text-blue-500 animate-pulse" />,
		SUCCESS: <CheckCircle2 className="h-4 w-4 text-green-500" />,
		FAILED: <XCircle className="h-4 w-4 text-red-500" />,
		SKIPPED: <Pause className="h-4 w-4 text-gray-400" />,
	};

	return icons[status] || <Clock className="h-4 w-4 text-gray-400" />;
};

export default function PlatformManagerPipelinePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [selectedPodcast, setSelectedPodcast] = useState<string | null>(null);
	const [chapters, setChapters] = useState<PipelineChapter[]>([]);
	const [loadingChapters, setLoadingChapters] = useState(false);

	const apiClient = BaseApiClient.getInstance();

	// Load active podcasts with TanStack Query
	const {
		data: podcasts = [],
		isLoading: loading,
		refetch,
	} = useQuery({
		queryKey: ["platformPipeline"],
		queryFn: async () => {
			try {
				// Fetch podcasts that are in PROCESSING state
				// Use platform manager endpoint to get all podcasts from all users
				const response = await apiClient.get<{
					success: boolean;
					podcasts: any[];
				}>("/platform-manager/podcasts");
				const allPodcasts = response.podcasts || [];

				// Filter to only show processing podcasts and fetch their pipeline status
				const processingPodcasts = allPodcasts.filter(
					(p: any) => p.status === "PROCESSING" || p.status === "PENDING",
				);

				// Fetch pipeline status for each podcast
				const podcastsWithStatus = await Promise.all(
					processingPodcasts.map(async (podcast: any) => {
						try {
							const pipelineResponse = await apiClient.get<any>(
								`/processing/podcasts/${podcast.id}/pipeline-status`,
							);
							return {
								id: podcast.id,
								title: podcast.title,
								status: podcast.status,
								totalChapters: pipelineResponse.overall.totalChapters,
								chaptersCompleted: pipelineResponse.overall.chaptersCompleted,
								createdAt: podcast.createdAt,
								user: {
									email: podcast.createdBy?.email || "Unknown",
									name: podcast.createdBy?.name || null,
								},
								pipeline: pipelineResponse.pipeline,
								health: pipelineResponse.health,
							};
						} catch (error) {
							console.error(
								`Failed to load pipeline status for podcast ${podcast.id}:`,
								error,
							);
							return null;
						}
					}),
				);

				return podcastsWithStatus.filter(
					(p) => p !== null,
				) as PipelinePodcast[];
			} catch (error) {
				console.error("Failed to load podcasts:", error);
				toast.error(t("platformManager.pipeline.errors.loadFailed"));
				throw error;
			}
		},
		refetchInterval: 10000, // Poll every 10s for admin monitoring
		refetchIntervalInBackground: false, // Pause when tab inactive
		staleTime: 0,
	});

	// Load chapter details for a specific podcast
	const loadChapterDetails = useCallback(
		async (podcastId: string) => {
			try {
				setLoadingChapters(true);
				const response = await apiClient.get<any>(
					`/processing/podcasts/${podcastId}/pipeline-status`,
				);
				setChapters(response.chapters);
				setSelectedPodcast(podcastId);
			} catch (error) {
				console.error("Failed to load chapter details:", error);
				toast.error(t("platformManager.pipeline.errors.loadChaptersFailed"));
			} finally {
				setLoadingChapters(false);
			}
		},
		[apiClient, t],
	);

	// Retry failed chapters
	const retryFailedChapters = useCallback(
		async (podcastId: string) => {
			try {
				await apiClient.post<
					{ message: string; retriedChapters: string[] },
					Record<string, never>
				>(`/processing/podcasts/${podcastId}/retry-failed`, {});
				toast.success(t("platformManager.pipeline.retrySuccess"));
				refetch();
				if (selectedPodcast === podcastId) {
					loadChapterDetails(podcastId);
				}
			} catch (error) {
				console.error("Failed to retry chapters:", error);
				toast.error(t("platformManager.pipeline.errors.retryFailed"));
			}
		},
		[apiClient, t, refetch, loadChapterDetails, selectedPodcast],
	);

	// Format duration
	const formatDuration = (ms: number | null) => {
		if (!ms) return "-";
		const seconds = Math.floor(ms / 1000);
		if (seconds < 60) return `${seconds}s`;
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}m ${remainingSeconds}s`;
	};

	// Define podcast table columns
	const podcastColumns: DataTableColumn<PipelinePodcast>[] = [
		{
			key: "title",
			label: t("platformManager.pipeline.columns.title"),
			sortable: true,
			accessor: (p) => p.title,
			render: (p) => (
				<div>
					<div className="font-medium">{p.title}</div>
					<div className="text-xs text-gray-500">{p.user.email}</div>
				</div>
			),
		},
		{
			key: "status",
			label: t("platformManager.pipeline.columns.status"),
			sortable: true,
			accessor: (p) => p.status,
			render: (p) => <StatusBadge status={p.status} />,
		},
		{
			key: "progress",
			label: t("platformManager.pipeline.columns.progress"),
			sortable: false,
			render: (p) => {
				const progress = (p.chaptersCompleted / p.totalChapters) * 100;
				return (
					<div className="space-y-1">
						<div className="flex justify-between text-xs">
							<span>
								{p.chaptersCompleted} / {p.totalChapters}
							</span>
							<span>{Math.round(progress)}%</span>
						</div>
						<Progress value={progress} className="h-2" />
					</div>
				);
			},
		},
		{
			key: "scriptStatus",
			label: t("platformManager.pipeline.columns.scriptStatus"),
			sortable: false,
			render: (p) => (
				<div className="text-xs space-y-0.5">
					<div className="flex items-center gap-1">
						<StatusIcon status="SUCCESS" />
						<span>{p.pipeline.scriptGeneration.success}</span>
					</div>
					<div className="flex items-center gap-1">
						<StatusIcon status="PROCESSING" />
						<span>{p.pipeline.scriptGeneration.processing}</span>
					</div>
					<div className="flex items-center gap-1">
						<StatusIcon status="PENDING" />
						<span>{p.pipeline.scriptGeneration.pending}</span>
					</div>
					<div className="flex items-center gap-1">
						<StatusIcon status="FAILED" />
						<span>{p.pipeline.scriptGeneration.failed}</span>
					</div>
				</div>
			),
		},
		{
			key: "audioStatus",
			label: t("platformManager.pipeline.columns.audioStatus"),
			sortable: false,
			render: (p) => (
				<div className="text-xs space-y-0.5">
					<div className="flex items-center gap-1">
						<StatusIcon status="SUCCESS" />
						<span>{p.pipeline.audioGeneration.success}</span>
					</div>
					<div className="flex items-center gap-1">
						<StatusIcon status="PROCESSING" />
						<span>{p.pipeline.audioGeneration.processing}</span>
					</div>
					<div className="flex items-center gap-1">
						<StatusIcon status="PENDING" />
						<span>{p.pipeline.audioGeneration.pending}</span>
					</div>
					<div className="flex items-center gap-1">
						<StatusIcon status="FAILED" />
						<span>{p.pipeline.audioGeneration.failed}</span>
					</div>
				</div>
			),
		},
		{
			key: "actions",
			label: t("platformManager.pipeline.columns.actions"),
			sortable: false,
			render: (p) => (
				<div className="flex gap-2">
					<Button
						size="sm"
						variant="outline"
						onClick={() => loadChapterDetails(p.id)}
					>
						{t("platformManager.pipeline.viewDetails")}
					</Button>
					{(p.pipeline.scriptGeneration.failed > 0 ||
						p.pipeline.audioGeneration.failed > 0) && (
						<Button
							size="sm"
							variant="outline"
							onClick={() => retryFailedChapters(p.id)}
						>
							<RefreshCw className="h-4 w-4" />
						</Button>
					)}
				</div>
			),
		},
	];

	// Define chapter table columns
	const chapterColumns: DataTableColumn<PipelineChapter>[] = [
		{
			key: "index",
			label: "#",
			sortable: true,
			accessor: (c) => c.index,
			render: (c) => <span className="font-mono">{c.index}</span>,
		},
		{
			key: "title",
			label: t("platformManager.pipeline.chapterColumns.title"),
			sortable: true,
			accessor: (c) => c.title,
		},
		{
			key: "scriptStatus",
			label: t("platformManager.pipeline.chapterColumns.script"),
			sortable: true,
			accessor: (c) => c.scriptStatus,
			render: (c) => (
				<div className="flex items-center gap-2">
					<StatusIcon status={c.scriptStatus} />
					<StatusBadge status={c.scriptStatus} />
					{c.timings.scriptDuration && (
						<span className="text-xs text-gray-500">
							{formatDuration(c.timings.scriptDuration)}
						</span>
					)}
				</div>
			),
		},
		{
			key: "audioStatus",
			label: t("platformManager.pipeline.chapterColumns.audio"),
			sortable: true,
			accessor: (c) => c.audioStatus,
			render: (c) => (
				<div className="flex items-center gap-2">
					<StatusIcon status={c.audioStatus} />
					<StatusBadge status={c.audioStatus} />
					{c.timings.audioDuration && (
						<span className="text-xs text-gray-500">
							{formatDuration(c.timings.audioDuration)}
						</span>
					)}
				</div>
			),
		},
		{
			key: "retryCount",
			label: t("platformManager.pipeline.chapterColumns.retries"),
			sortable: true,
			accessor: (c) => c.retryCount,
			render: (c) =>
				c.retryCount > 0 ? (
					<Badge variant="secondary">{c.retryCount}</Badge>
				) : (
					"-"
				),
		},
		{
			key: "error",
			label: t("platformManager.pipeline.chapterColumns.error"),
			sortable: false,
			render: (c) =>
				c.lastError ? (
					<div
						className="max-w-xs truncate text-xs text-red-600"
						title={c.lastError}
					>
						{c.lastError}
					</div>
				) : (
					"-"
				),
		},
	];

	return (
		<div className="container mx-auto p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => navigate("/platform-manager")}
					>
						<ArrowLeft className="h-5 w-5" />
					</Button>
					<div>
						<h1 className="text-3xl font-bold">
							{t("platformManager.pipeline.title")}
						</h1>
						<p className="text-gray-600">
							{t("platformManager.pipeline.description")}
						</p>
					</div>
				</div>
				<Button onClick={() => refetch()} variant="outline" disabled={loading}>
					<RefreshCw
						className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
					/>
					{t("common.refresh")}
				</Button>
			</div>

			{/* Active Podcasts Table */}
			<DataTable
				data={podcasts}
				columns={podcastColumns}
				title={t("platformManager.pipeline.activePodcasts")}
				description={t("platformManager.pipeline.activePodcastsDesc")}
				searchable
				searchPlaceholder={t("platformManager.pipeline.searchPlaceholder")}
				defaultSortField="createdAt"
				defaultSortOrder="desc"
				loading={loading}
				emptyMessage={t("platformManager.pipeline.noPodcastsProcessing")}
			/>

			{/* Chapter Details */}
			{selectedPodcast && chapters.length > 0 && (
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>
									{t("platformManager.pipeline.chapterDetails")}
								</CardTitle>
								<CardDescription>
									{podcasts.find((p) => p.id === selectedPodcast)?.title}
								</CardDescription>
							</div>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									setSelectedPodcast(null);
									setChapters([]);
								}}
							>
								{t("common.close")}
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						<DataTable
							data={chapters}
							columns={chapterColumns}
							searchable={false}
							defaultSortField="index"
							defaultSortOrder="asc"
							loading={loadingChapters}
							emptyMessage={t("platformManager.pipeline.noChapters")}
						/>

						{/* Health Warnings */}
						{selectedPodcast &&
							(() => {
								const podcast = podcasts.find((p) => p.id === selectedPodcast);
								if (!podcast) return null;

								return (
									<div className="mt-4 space-y-2">
										{podcast.health.warnings.length > 0 && (
											<div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
												<AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
												<div className="flex-1">
													<p className="font-medium text-sm text-yellow-800">
														{t("platformManager.pipeline.warnings")}
													</p>
													<ul className="list-disc list-inside text-sm text-yellow-700 mt-1">
														{podcast.health.warnings.map((warning, i) => (
															<li key={i}>{warning}</li>
														))}
													</ul>
												</div>
											</div>
										)}

										{podcast.health.recommendations.length > 0 && (
											<div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
												<CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
												<div className="flex-1">
													<p className="font-medium text-sm text-blue-800">
														{t("platformManager.pipeline.recommendations")}
													</p>
													<ul className="list-disc list-inside text-sm text-blue-700 mt-1">
														{podcast.health.recommendations.map((rec, i) => (
															<li key={i}>{rec}</li>
														))}
													</ul>
												</div>
											</div>
										)}
									</div>
								);
							})()}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
